import React, { useState, useRef, useEffect, useMemo } from "react";
import { Typography, Space, Switch, Empty, Spin } from "antd";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, getElementAtEvent } from "react-chartjs-2";
import { useSelector, useDispatch } from 'react-redux';
import { getInstituteChartData } from '../../../actions';

const { Text } = Typography;

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export const options = (selectedInstitute, apiData, sortedApiData, stepSize = 1) => ({
  maintainAspectRatio: false,
  indexAxis: "y",
  responsive: true,
  layout: {
    padding: { left: 10 },
  },
  categoryPercentage: 0.8,
  barPercentage: 0.9,
  onHover: (event, activeElements) => {
    event.native.target.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
  },
  plugins: {
    legend: { display: false },
    title: { display: false },
    tooltip: {
      position: 'nearest',
      intersect: true,
      mode: 'nearest',
      callbacks: {
        title: function(context) {
          // Get the index from the tooltip context
          const index = context[0]?.dataIndex;
          // Return the full institute name from SORTED data (what's actually displayed)
          if (index !== undefined && sortedApiData[index]) {
            return sortedApiData[index].institute;
          }
          return context[0]?.label;
        },
        label: function(context) {
          const index = context[0]?.dataIndex;
          const datasetIndex = context[0]?.datasetIndex;
          const dataset = context.dataset;
          // const value = context[0]?.parsed[datasetIndex];
          const value = context.parsed.x;
          // Use the dataset label directly for accurate identification
          const label = dataset.label || 'Unknown';
          return `${label}: ${value}`;
        }
      }
    },
  },
  scales: {
    x: {
      grid: {
        display: true,
        color: "#E5E5E5",
        borderColor: "#CFCFCF",
        borderWidth: 1,
        drawBorder: true,
        drawOnChartArea: false,
      },
      title: {
        display: false,
      },
      ticks: {
        color: "#444",
        font: { size: 12 },
        stepSize: stepSize,
        callback: function(value) {
          return Number.isInteger(value) ? value : '';
        },
      },
    },
    y: {
      grid: {
        display: false,
        borderColor: "#CFCFCF",
        borderWidth: 1,
        drawBorder: true,
        drawOnChartArea: false,
      },
      title: {
        display: false,
      },
      ticks: {
        color: (context) => {
          // Use index-based selection since all names are "Not Specified"
          // Extract index from selectedInstitute if it's in format "institute_0", "institute_1", etc.
          if (selectedInstitute && selectedInstitute.startsWith('institute_')) {
            const selectedIndex = parseInt(selectedInstitute.split('_')[1], 10);
            if (context.index === selectedIndex) {
              return "#001F54";
            }
          }
          return "#666";
        },
        font: {
          weight: (context) => {
            // Use index-based selection since all names are "Not Specified"
            // Extract index from selectedInstitute if it's in format "institute_0", "institute_1", etc.
            if (selectedInstitute && selectedInstitute.startsWith('institute_')) {
              const selectedIndex = parseInt(selectedInstitute.split('_')[1], 10);
              if (context.index === selectedIndex) {
                return "bold";
              }
            }
            return "normal";
          },
          size: 12,
        },
        padding: 2,
      },
    },
  },
});

// Use API data instead of hardcoded data

// Calculate nice step size for x-axis based on max value
const calculateStepSize = (maxValue) => {
  if (maxValue <= 5) return 1;
  const niceSteps = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000];
  const targetTicks = 6;
  const rawStep = maxValue / targetTicks;
  return niceSteps.find(s => s >= rawStep) || Math.ceil(rawStep / 100) * 100;
};

// Function to truncate text and append "..." if it exceeds maxLength
const truncateText = (text, maxLength = 20) => {
  if (!text || text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength) + "...";
};

const InstituteBar = ({ selectedInstitute, onInstituteClick, onClearSelection, selectedFilter, isApplied, onApplicantsToggleChange }) => {
  const dispatch = useDispatch();
  const instituteChartData = useSelector(state => state?.corporateDashboardPage2?.instituteChartData);
  const instituteChartLoading = useSelector(state => state?.corporateDashboardPage2?.instituteChartLoading);
  
  const handleToggleChange = (checked) => {
    // Use parent's toggle handler
    if (onApplicantsToggleChange) {
      onApplicantsToggleChange(checked);
    }
    // Trigger institute-chart API with isApplied parameter
    dispatch(getInstituteChartData(selectedFilter, null, checked));
  };
  const [selectedData, setSelectedData] = useState({
    label: "None",
    hires: 0,
    applicants: 0,
  });

  const chartRef = useRef(null);

  
  // Use API data instead of hardcoded data
  const apiData = instituteChartData?.instituteBarChart || [];
  
  // Check if there's any institute data
  const hasInstituteData = apiData && apiData.length > 0 && 
    apiData.some(item => (isApplied ? item.applicants : item.hired) > 0);
  
  // Sort API data based on selected metric (descending)
  const sortedApiData = useMemo(() => {
    const sorted = [...apiData].sort((a, b) => {
      if (isApplied) {
        return b.applicants - a.applicants; // descending by applicants
      } else {
        return b.hired - a.hired; // descending by hired
      }
    });
    return sorted.slice(0, 10); // Top 10
  }, [apiData, isApplied]);
  
  // Memoize derived data to prevent infinite re-renders
  const { labels, hiredData, applicantsData } = useMemo(() => {
    return {
      labels: sortedApiData.map(item => truncateText(item.institute, 15)),
      hiredData: sortedApiData.map(item => item.hired),
      applicantsData: sortedApiData.map(item => item.applicants),
    };
  }, [sortedApiData]);

  // Calculate dynamic step size for x-axis
  const maxDataValue = Math.max(
    ...applicantsData,
    ...hiredData,
    0
  );
  const stepSize = calculateStepSize(maxDataValue);

  const data = {
    labels,
    datasets: [
      {
        label: "Applicants",
        data: applicantsData,
        datalabels: { display: false },
        backgroundColor: "#a0dcfc",
        barThickness: 10,
        order: 2,
      },
      {
        label: "Hires",
        data: hiredData,
        datalabels: { display: false },
        backgroundColor: "#001F54",
        barThickness: 10,
        order: 1,
      },
    ],
  };
  const handleClearSelection = () => {
    if (onClearSelection) {
      onClearSelection();
    }
  };


  useEffect(() => {
    let selectedIndex = null;
    if (selectedInstitute && selectedInstitute.startsWith('institute_')) {
      selectedIndex = parseInt(selectedInstitute.split('_')[1], 10);
    }
    
    if (selectedIndex !== null && selectedIndex < labels.length) {
      // Use the full institute name from sortedApiData for the selected data display
      const fullInstituteName = sortedApiData[selectedIndex]?.institute || labels[selectedIndex];
      setSelectedData({
        label: fullInstituteName,
        hires: hiredData[selectedIndex],
        applicants: applicantsData[selectedIndex],
      });
    } else if (!selectedInstitute) {
      setSelectedData({
        label: "None",
        hires: 0,
        applicants: 0,
      });
    }
  }, [selectedInstitute, isApplied, labels, hiredData, applicantsData, sortedApiData]);


  const onClick = (event) => {
    const chart = chartRef.current;
    if (!chart) return;
    const element = getElementAtEvent(chart, event);
    if (element && element.length > 0) {
      const { datasetIndex, dataIndex } = element[0];
      // Pass dataset info to distinguish between Applicants and Hires
      if (onInstituteClick) {
        onInstituteClick(event, element, { 
          datasetIndex, 
          dataIndex,
          isApplicantsBar: datasetIndex === 0,
          isHiresBar: datasetIndex === 1
        });
      }
    }
  };

  return (
    <div
      style={{
        width: "100%",
        padding: "8px 0px 16px 0",
        fontSize: 13,
        borderRadius: 8,
        height: 500,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        justifyContent: "flex-start",
      }}
    >
      <style>{`
        .full-height-spin-wrapper {
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        .full-height-spin-wrapper .ant-spin-blur {
          opacity: 0.5;
          user-select: none;
        }
        .full-height-spin-wrapper .ant-spin-container {
          height: 100%;
          display: flex;
          flex-direction: column;
        }
      `}</style>
      <Spin spinning={instituteChartLoading} wrapperClassName="full-height-spin-wrapper">
        {/* Header with Toggle */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "8px" }}>
          <Text style={{ fontSize: 20, fontWeight: 500, margin: 0 }}>Top 10 Institutes</Text>
          
          {/* Toggle Switch */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Switch
              checked={isApplied}
              onChange={handleToggleChange}
              style={{
                width: 39,
                height: 22,
                transform: "rotate(0deg)",
                opacity: 1,
                borderRadius: 20,
                backgroundColor: isApplied ? "rgba(37, 99, 235, 1)" : "#ccc",
              }}
            />
            <Text style={{ fontSize: 14, color: "#1a2645", whiteSpace: "nowrap" }}>
              {isApplied ? "Applicants" : "Hires"}
            </Text>
          </div>
        </div>

      {hasInstituteData ? (
        <>
      {/* Chart */}
      <div
        style={{
          flexGrow: 1,
          width: "100%", // ✅ full width to extend to scroll buttons
          marginTop: 12,
          overflow: "hidden",
          position: "relative",
          display: "flex",
        }}
      >
        {/* Institutes Label - Outside Chart */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "20px",
            paddingRight: "0px",
          }}
        >
          <Text
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#000",
              transform: "rotate(-90deg)",
              whiteSpace: "nowrap",
            }}
          >
            Institutes
          </Text>
        </div>
        
        <div
          style={{
            flexGrow: 1,
            height: "100%",
            position: "relative",
          }}
        >
          <Bar options={options(selectedInstitute, apiData, sortedApiData, stepSize)} data={data} ref={chartRef} onClick={onClick} />
        </div>
      </div>

      {/* === Applicants / Hires line + arrows (on same line) === */}
      <div
        style={{
          marginTop: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        {/* Left: Label + line */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexGrow: 2,
            gap: 10,
            justifyContent: "flex-end",
          }}
        >
          <Text
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#000",
              whiteSpace: "nowrap",
            }}
          >
            Applicants/Hires
          </Text>
          <div
            style={{
              flexGrow: 0,
              width: "67%", // ✅ extended horizontal line to match chart width
              height: 1,
              backgroundColor: "#d1d1d1",
            }}
          />
        </div>

      </div>

      {/* Legend */}
      <div
        style={{
          marginTop: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 20,
          marginLeft: 45,
        }}
      >
        <Space align="center" size={6}>
          <div
            style={{
              width: 14,
              height: 14,
              backgroundColor: "#a0dcfc",
              borderRadius: "50%",
            }}
          />
          <Text style={{ fontSize: 12, color: "#1a2645" }}>Applicants</Text>
        </Space>

        <Space align="center" size={6}>
          <div
            style={{
              width: 14,
              height: 14,
              backgroundColor: "#001F54",
              borderRadius: "50%",
            }}
          />
          <Text style={{ fontSize: 12, color: "#1a2645" }}>Hires</Text>
        </Space>
      </div>

      {/* Clear button above details box */}
      {onClearSelection && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
          <Text
            style={{
              fontSize: '11px',
              color: selectedInstitute ? 'red' : 'grey',
              cursor: 'pointer'
            }}
            onClick={handleClearSelection}
          >
            Clear
          </Text>
        </div>
      )}

      {/* Selected Info Box */}
      <div
        style={{
          marginTop: 12,
          backgroundColor: "#fff",
          border: selectedInstitute ? "1px solid #435fe8" : "1px solid rgba(194, 194, 194, 1)",
          borderRadius: '8px',
          padding: '12px 16px',
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {selectedInstitute ? (
          <>
            <Text style={{ fontSize: 12 }}>
              Selected:{" "}
              <Text strong style={{ color: "#435fe8" }}>
                {selectedData.label}
              </Text>
            </Text>

            <Space size={10}>
              <Space align="center" size={4}>
                <div
                  style={{
                    width: 14,
                    height: 14,
                    backgroundColor: "#a0dcfc",
                    borderRadius: "50%",
                  }}
                />
                <Text style={{ fontSize: 12 }}>
                  {selectedData.applicants} Applicants
                </Text>
              </Space>

              <Space align="center" size={4}>
                <div
                  style={{
                    width: 14,
                    height: 14,
                    backgroundColor: "#001F54",
                    borderRadius: "50%",
                  }}
                />
                <Text style={{ fontSize: 12 }}>
                  {selectedData.hires} Hires
                </Text>
              </Space>
            </Space>
          </>
        ) : (
          <Space size={8}>
            <svg width="18" height="21" viewBox="0 0 18 21" style={{ marginLeft: '2px' }}>
              <rect 
                x="10.05" y="3" width="4.022172927856445" height="12" fill="rgba(50, 73, 215, 1)"opacity="1"
              />
              <rect 
                x="10.05" y="17" width="4.022172927856445" height="4" fill="rgba(50, 73, 215, 1)" opacity="1"
              />
            </svg>
            <Text style={{ 
              color: '#262626',
              fontFamily: 'Plus Jakarta Sans',fontWeight: 400,fontSize: 14,lineHeight: '18px',letterSpacing: '0%'
            }}>
              Note: Click the graph for more details.
            </Text>
          </Space>
        )}
      </div>
        </>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px', flexGrow: 1 }}>
          <Empty />
        </div>
      )}
      </Spin>
    </div>
  );
};

export default InstituteBar;
